import React, { useState, useRef, useEffect } from 'react';
import { CustomIcon } from '../../UI';
import './PhotoUploader.scss';
import PhotosModal from '../../modals/slider/PhotosModal';
import fileService from '../../../services/fileService';

const PhotoUploader = ({ 
  title = "Photos", 
  photos = [], 
  onPhotosChange,
  onDeletedKeysChange, // Callback when images with keys are deleted
  maxFileSize = 120,
  acceptedTypes = "image/png,image/jpeg,image/jpg",
  showCount = true,
  className = ""
}) => {
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef(null);

  const buildPhotoId = (photo, index) => {
    if (photo?.id) return photo.id;
    const candidate = photo?.key || photo?.url || photo?.name;
    if (candidate) return `photo-${candidate}`;
    return `photo-${index}`;
  };

  const extractKeyFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;

    if (url.startsWith('blob:')) return null;

    if (url.startsWith('/api/files/') || url.startsWith('/files/')) {
      let path = url.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
      if (path.startsWith('signed-url/')) {
        path = path.replace(/^signed-url\//, '');
        try {
          return decodeURIComponent(path.split('?')[0]);
        } catch (e) {
          return path.split('?')[0];
        }
      }
      return path;
    }

    if (url.includes('.amazonaws.com/')) {
      const parts = url.split('.amazonaws.com/');
      if (parts.length > 1) {
        let key = parts[1];
        if (key.includes('?')) key = key.split('?')[0];
        if (key.includes('#')) key = key.split('#')[0];
        return key || null;
      }
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const parsed = new URL(url);
        let path = parsed.pathname || '';
        if (path.startsWith('/')) path = path.slice(1);

        if (path.startsWith('api/files/signed-url/') || path.startsWith('files/signed-url/')) {
          path = path.replace(/^(api\/)?files\/signed-url\//, '');
          try {
            return decodeURIComponent(path.split('?')[0]);
          } catch (e) {
            return path.split('?')[0];
          }
        }

        if (path.startsWith('api/files/')) {
          path = path.replace(/^api\/files\//, '');
        }
        if (path.startsWith('files/')) {
          path = path.replace(/^files\//, '');
        }
        return path || null;
      } catch (e) {
        return null;
      }
    }

    if (url.includes('?')) {
      return url.split('?')[0];
    }

    return url;
  };

  const isSignedUrlEndpoint = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('/signed-url/') || url.includes('/signed-urls');
  };

  const normalizePhoto = (photo, index) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      const key = extractKeyFromUrl(photo);
      const isSignedUrl = photo.includes('.amazonaws.com/') && photo.includes('X-Amz-Signature');
      const isBlobUrl = photo.startsWith('blob:');
      const isHttpUrl = photo.startsWith('http://') || photo.startsWith('https://');
      const isEndpoint = isSignedUrlEndpoint(photo);

      return {
        id: key ? `key-${key}` : `photo-${index}`,
        key: key || null,
        url: (isBlobUrl || (isHttpUrl && !isEndpoint && isSignedUrl)) ? photo : '',
        alt: `Image ${index + 1}`,
        name: key ? key.split('/').pop() : `Image ${index + 1}`,
        needsRefresh: isSignedUrl || isEndpoint
      };
    }
    if (typeof photo === 'object') {
      const url = photo.url || '';
      const extractedKey = extractKeyFromUrl(url);
      const key = photo.key || extractedKey || (typeof photo === 'string' ? extractKeyFromUrl(photo) : null);
      const isSignedUrl = url.includes('.amazonaws.com/') && url.includes('X-Amz-Signature');
      const isBlobUrl = url.startsWith('blob:');
      const isHttpUrl = url.startsWith('http://') || url.startsWith('https://');
      const isEndpoint = isSignedUrlEndpoint(url);

      const normalized = {
        ...photo,
        id: buildPhotoId(photo, index),
        key: key || null,
        url: (isBlobUrl || (isHttpUrl && !isEndpoint && isSignedUrl)) ? url : '',
        alt: photo.alt || photo.name || `Image ${index + 1}`,
        name: photo.name || (key ? key.split('/').pop() : undefined),
        needsRefresh: isSignedUrl || isEndpoint || photo.needsRefresh
      };
      if (photo.file instanceof File && !normalized.url) {
        normalized.url = URL.createObjectURL(photo.file);
      }
      return normalized;
    }
    return null;
  };

  const normalizePhotos = (inputPhotos = []) => {
    if (!Array.isArray(inputPhotos)) return [];
    return inputPhotos.map(normalizePhoto).filter(Boolean);
  };

  const resolveSignedUrls = async (normalized) => {
    const keysToSign = normalized
      .filter((photo) => {
        if (photo?.url?.startsWith('blob:')) return false;
        if (!photo?.key) return false;
        const url = photo.url || '';
        const isEndpoint = isSignedUrlEndpoint(url);
        const isSigned = url.includes('.amazonaws.com/') && url.includes('X-Amz-Signature');
        return !isSigned || isEndpoint;
      })
      .map((photo) => photo.key)
      .filter(Boolean);

    if (keysToSign.length === 0) {
      const photosWithUrls = normalized.filter(p => p.url);
      if (photosWithUrls.length === normalized.length) {
        setUploadedPhotos(normalized);
        return;
      }
    }

    try {
      const urlMap = keysToSign.length > 0 ? await fileService.getSignedUrls(keysToSign) : {};
      const updated = normalized.map((photo) => {
        if (photo.key && urlMap[photo.key]) {
          return { ...photo, url: urlMap[photo.key], needsRefresh: false };
        }
        if (photo.url && (photo.url.startsWith('blob:') || photo.url.startsWith('http'))) {
          return photo;
        }
        if (photo.key && !photo.url) {
          return { ...photo, needsRefresh: true };
        }
        return photo;
      });
      setUploadedPhotos(updated);
    } catch (error) {
      console.error('[PhotoUploader] Error refreshing signed URLs:', error);
      setUploadedPhotos(normalized.map(photo => ({ ...photo, needsRefresh: true })));
    }
  };

  const prevPhotosSignatureRef = useRef('');

  useEffect(() => {
    if (!photos || !Array.isArray(photos)) {
      setUploadedPhotos([]);
      prevPhotosSignatureRef.current = '';
      return;
    }
    const normalized = normalizePhotos(photos);
    const signature = normalized.map((p) => p?.key ?? p?.id ?? p?.url ?? '').filter(Boolean).join('|');
    if (prevPhotosSignatureRef.current === signature) return;
    prevPhotosSignatureRef.current = signature;
    resolveSignedUrls(normalized);
  }, [photos]);

  const uploadedPhotosRef = useRef(uploadedPhotos);
  uploadedPhotosRef.current = uploadedPhotos;

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      const current = uploadedPhotosRef.current;
      const photosWithKeys = current.filter((p) => p.key && !p.url?.startsWith('blob:'));
      if (photosWithKeys.length > 0) resolveSignedUrls(current);
    }, 4 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    const current = uploadedPhotosRef.current;
    const photosNeedingUrl = current.filter((p) => p.key && !p.url && !p.needsRefresh);
    if (photosNeedingUrl.length === 0) return;

    const fetchUrls = async () => {
      const promises = photosNeedingUrl.map(async (photo) => {
        try {
          const newUrl = await fileService.getSignedUrl(photo.key);
          if (newUrl) return { id: photo.id, url: newUrl };
        } catch (error) {
          console.error('[PhotoUploader] Failed to get URL for photo:', photo.id, error);
        }
        return null;
      });

      const results = await Promise.all(promises);
      const updates = results.filter(Boolean);
      if (updates.length > 0) {
        setUploadedPhotos((prev) =>
          prev.map((p) => {
            const update = updates.find((u) => u.id === p.id);
            return update ? { ...p, url: update.url, needsRefresh: false } : p;
          })
        );
      }
    };

    fetchUrls();
  }, [uploadedPhotos]);

  const handleImageError = async (photoId) => {
    const photo = uploadedPhotos.find(p => p.id === photoId);
    if (!photo) return;

    if (photo.key) {
      try {
        const newUrl = await fileService.getSignedUrl(photo.key);
        if (newUrl) {
          setUploadedPhotos(prev => prev.map(p => 
            p.id === photoId ? { ...p, url: newUrl, needsRefresh: false } : p
          ));
          return;
        }
      } catch (error) {
        console.error('[PhotoUploader] Failed to refresh URL for photo:', photoId, error);
      }
    }

    if (!photo.url || photo.url === '') {
      const extractedKey = extractKeyFromUrl(photo.url || '');
      if (extractedKey) {
        try {
          const newUrl = await fileService.getSignedUrl(extractedKey);
          if (newUrl) {
            setUploadedPhotos(prev => prev.map(p => 
              p.id === photoId ? { ...p, url: newUrl, key: extractedKey, needsRefresh: false } : p
            ));
          }
        } catch (error) {
          console.error('[PhotoUploader] Failed to refresh URL from extracted key:', photoId, error);
        }
      }
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Reset input value to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }

    setIsUploading(true);
    
    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newPhotos = files.map((file, index) => ({
      id: `file-${Date.now()}-${index}`,
      url: URL.createObjectURL(file),
      alt: file.name,
      name: file.name,
      file: file
    }));
    
    const updatedPhotos = [...uploadedPhotos, ...newPhotos];
    setUploadedPhotos(updatedPhotos);
    onPhotosChange?.(updatedPhotos);
    setIsUploading(false);
  };

  // Handle browse file click
  const handleBrowseFile = () => {
    fileInputRef.current?.click();
  };

  // Handle photo click to open modal
  const handlePhotoClick = (index) => {
    setSelectedPhotoIndex(index);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Handle photo removal
  const handleRemovePhoto = (photoId) => {
    const photoToRemove = uploadedPhotos.find(photo => photo.id === photoId);
    
    // If photo has a key (not a new File object), track it as deleted
    // Key can be in photo.key or extracted from photo.url if it's a server URL
    if (!photoToRemove?.file) {
      let deletedKey = null;
      
      // Try to get key from photo.key
      if (photoToRemove?.key) {
        deletedKey = photoToRemove.key;
      } 
      // Try to extract key from URL if it's a server URL (not blob)
      else if (photoToRemove?.url && typeof photoToRemove.url === 'string') {
        const url = photoToRemove.url;
        // If it's a server URL (not blob), try to extract key
        if (!url.startsWith('blob:') && (url.startsWith('/api/files/') || url.startsWith('/files/') || url.includes('.amazonaws.com'))) {
          // Extract key from URL
          let key = url;
          if (key.startsWith('/api/files/')) {
            key = key.replace(/^\/api\/files\//, '');
          } else if (key.startsWith('/files/')) {
            key = key.replace(/^\/files\//, '');
          } else if (key.includes('.amazonaws.com/')) {
            key = key.split('.amazonaws.com/')[1];
            if (key.includes('?')) key = key.split('?')[0];
          }
          deletedKey = key;
        }
      }
      
      // If we found a key, track it as deleted
      if (deletedKey) {
        onDeletedKeysChange?.(deletedKey);
      }
    }
    
    // Clean up blob URL if it exists
    if (photoToRemove?.url && photoToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(photoToRemove.url);
    }
    
    const updatedPhotos = uploadedPhotos.filter(photo => photo.id !== photoId);
    setUploadedPhotos(updatedPhotos);
    onPhotosChange?.(updatedPhotos);
  };

  return (
    <div className={`photo-uploader ${className}`}>
      {/* Header */}
      <div className="photos-header">
        <h3 className="form-section-title">{title}</h3>
        {showCount && uploadedPhotos.length > 0 && (
          <span className="photos-count">
            {uploadedPhotos.length} photo{uploadedPhotos.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      
      {/* Photo Gallery */}
      {uploadedPhotos.length > 0 && (
        <div className="photo-gallery">
          {uploadedPhotos.map((photo, index) => (
            <div key={photo.id} className="photo-item">
              <div 
                className="photo-preview"
                onClick={() => handlePhotoClick(index)}
              >
                {photo.url ? (
                  <img 
                    src={photo.url} 
                    alt={photo.alt}
                    className="photo-image"
                    onError={() => handleImageError(photo.id)}
                    loading="lazy"
                  />
                ) : (
                  <div className="photo-placeholder">
                    <span>{photo.name || photo.alt || `Image ${index + 1}`}</span>
                  </div>
                )}
                <div className="photo-overlay">
                  <CustomIcon name="eye" size={24} color="white" type='svg' className='photo-overlay-image'/>
                </div>
              </div>
              <button 
                type="button"
                className="remove-photo-btn"
                onClick={() => handleRemovePhoto(photo.id)}
                title="Remove photo"
              >
                <CustomIcon name="close" size={16} />
              </button>
            </div>
          ))}
          
          {/* Add more photos button */}
          <div className="add-photo-item" onClick={handleBrowseFile}>
            <CustomIcon name="plus-01" size={32} type='svg' />
            <span className="add-photo-text">Add Photo</span>
          </div>
        </div>
      )}

      {/* Upload Area - shown when no photos */}
      {uploadedPhotos.length === 0 && (
        <div className="photo-upload-area">
          <div className="upload-zone" onClick={handleBrowseFile}>
            <CustomIcon name="share" size={48} color="gray-40" type='svg' />
            <p className="upload-text">Max {maxFileSize} MB, PNG, JPEG</p>
            <button type="button" className="browse-file-btn">
              Browse File
            </button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <p className="progress-text">Uploading photos...</p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {/* Vehicle Photos Modal */}
      <PhotosModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        photos={uploadedPhotos}
        title={title}
      />
    </div>
  );
};

export default PhotoUploader;
